<template>
  <Dialog :visible="true" modal :style="{ width: '48rem' }" :breakpoints="{ '767px': '94vw' }"
    :draggable="false" :closable="!pending" :closeButtonProps="{ 'aria-label': copy.close }" :closeOnEscape="!pending"
    class="jf-auth-dialog jf-profile-choice" :aria-labelledby="titleId" @update:visible="cancelChoice">
    <button type="button" class="jf-role-back" :disabled="pending" @click="cancelChoice">
      <i class="pi pi-arrow-left" aria-hidden="true" /> {{ copy.back }}
    </button>
    <h1 :id="titleId" class="jf-role-title">{{ copy.title }}</h1>
    <p :id="titleId + '-intro'" class="jf-role-intro">{{ copy.intro }}</p>
    <form @submit.prevent="confirmChoice">
      <fieldset class="jf-role-group" :disabled="pending" :aria-describedby="titleId + '-intro'">
        <legend class="sr-only">{{ copy.title }}</legend>
        <div class="jf-role-cards">
          <label v-for="role in availableRoles" :key="role.name" class="jf-role-card" :class="{ 'is-selected': selected === role.name }">
            <i :class="['pi', role.icon]" aria-hidden="true" />
            <strong>{{ copy.roles[role.name].title }}</strong>
            <span>{{ copy.roles[role.name].description }}</span>
            <input v-model="selected" type="radio" :name="titleId" :value="role.name" :aria-label="copy.roles[role.name].title" />
          </label>
        </div>
      </fieldset>
      <p v-if="!availableRoles.length" role="status" class="jf-role-message">{{ copy.empty }}</p>
      <p v-if="failed" role="alert" class="jf-role-message">{{ copy.failed }}</p>
      <Button type="submit" :label="pending ? copy.pending : copy.continue" :disabled="!canContinue || pending" :loading="pending" class="jf-role-continue" />
    </form>
  </Dialog>
</template>

<script setup>
  import { computed, getCurrentInstance, ref } from "vue";
  import { profileChoiceCopy } from "@/composables/profileChoiceCopy.mjs";
  import Dialog from "primevue/dialog";
  import Button from "primevue/button";
  import store from "@/store";
  import requestHandler from "@/composables/requestHandler";
  import Cookies from "js-cookie";
  import { encrypte } from "@/composables/crypto";
  import { getRegistrationProfile } from "@/composables/registrationProfiles.mjs";

  const props = defineProps({
    action: {
      type: String,
      required: false,
      default: 'login'
    }
  });

  const selected = ref(null);
  const pending = ref(false);
  const failed = ref(false);
  const titleId = 'profile-choice-' + getCurrentInstance().uid;
  const copy = computed(() => profileChoiceCopy(store.getters.getUserLang));
  const roles = [{ name: 'professional', icon: 'pi-user' }, { name: 'company', icon: 'pi-building' }, { name: 'recruiter', icon: 'pi-users' }];
  const availableRoles = computed(() => roles.filter(role => canDisplay(role.name)));
  const canContinue = computed(() => availableRoles.value.some(role => role.name === selected.value));
  function cancelChoice() { if (!pending.value) emit('cancel'); }
  async function confirmChoice() {
    if (pending.value || !canContinue.value) return;
    const profile = selected.value;
    if (props.action !== 'register' && !store.getters.getUserType.includes(profile)) return;
    pending.value = true;
    failed.value = false;
    try { await performAction(profile); }
    catch { failed.value = true; }
    finally { pending.value = false; }
  }

  async function performAction(profileName = ''){
    switch(props.action){
      case 'login':
        await loginOnProfile(profileName);
      break;
      case 'register':
        return emitProfileChosen(profileName);
      case 'switchAccount':
        await changeProfile(profileName);
      break;
    }
  }

  async function loginOnProfile(profileName = ''){
    let userProfiles = store.getters.getUserType;
    if(!userProfiles.includes(profileName))
        profileName = 'profile';
    store.commit('setLoggedAs', profileName);
    Cookies.set('last_login_as', encrypte(profileName));
    let urlToGo = store.getters.getRouteToGoAfterLogin;
    if(urlToGo !== null){
      store.dispatch('saveRouteToGoAfterLogin', {url: null});
      await requestHandler.redirect(urlToGo);
      return;
    }
    if(profileName === 'professional') {
      await requestHandler.redirect(`/dashboard/${store.getters.getLoggedAs}/${store.getters.getUserSlug}/preview/curriculum`);
    } else {
      await requestHandler.redirect(`/dashboard/${store.getters.getLoggedAs}/${store.getters.getUserSlug}`);
    }
  }

  async function changeProfile(profileName = ''){
    let userProfiles = store.getters.getUserType;
    if(!userProfiles.includes(profileName))
        profileName = 'profile';
    store.commit('setLoggedAs', profileName);
    Cookies.set('last_login_as', encrypte(profileName));
    emitProfileChosen(profileName);
    await requestHandler.redirect(`/dashboard/${store.getters.getLoggedAs}/${store.getters.getUserSlug}`);
  }

  const emit = defineEmits({
    click: null,
    cancel: null,
    profileChosen: ({ profileName }) => {
      return profileName;
    }
  })

  function emitProfileChosen(profileName = ''){
    const selectedProfile = {
      app: profileName,
      api: getRegistrationProfile(profileName)
    }
    if(!selectedProfile.api)
      return false;
    emit('profileChosen', { profileName: selectedProfile })
    return true;
  }

  function canDisplay(name = ''){
    let myProfiles = store.getters.getLoggedUserNotLoggedInProfile;
    let loggedAs = store.getters.getLoggedAs;
    let display = false;
    switch(props.action){
      case 'register':
        display = true;
      break;
      case 'switchAccount':
        if(myProfiles.length < 1 || name == loggedAs || !myProfiles.includes(name)){
          display = false;
        }else{
          display = true;
        }
      break;
      case 'login':
        if(name == 'professional' && store.getters.getProfessional != null){
          display = true;
        }
        if(name == 'company' && store.getters.getCompany != null){
          display = true;
        }
        if(name == 'recruiter' && store.getters.getRecruiter != null){
          display = true;
        }
      break;
    }
    return display;
  }
</script>

<style scoped>
.jf-role-back { display: inline-flex; align-items: center; gap: .5rem; min-height: 44px; color: var(--jf-navy-900, #0b1c47); padding: .5rem; border-radius: .5rem; }
.jf-role-title { text-align: center; font-size: 1.6rem; font-weight: 700; color: var(--jf-navy-900, #0b1c47); margin: .5rem 0; }
.jf-role-intro { text-align: center; color: #435575; margin-bottom: 1.5rem; }
.jf-role-group { min-width: 0; }
.jf-role-cards { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .75rem; }
.jf-role-card { display: flex; flex-direction: column; align-items: center; gap: .75rem; padding: 1.25rem .75rem; border: 2px solid #d6dfea; border-radius: .65rem; color: var(--jf-navy-900, #0b1c47); text-align: center; cursor: pointer; background: white; }
.jf-role-card > i { font-size: 1.8rem; }
.jf-role-card > span { font-size: .9rem; line-height: 1.5; flex: 1; }
.jf-role-card input { width: 20px; height: 20px; accent-color: #087c89; }
.jf-role-card.is-selected { border-color: #087c89; background: #edf9fa; }
.jf-role-card:focus-within, .jf-role-back:focus-visible, .jf-role-continue:focus-visible { outline: 3px solid var(--jf-navy-900, #0b1c47); outline-offset: 3px; }
.jf-role-continue { display: flex; justify-content: center; width: 100%; min-height: 44px; margin-top: 1.5rem; padding: .75rem 1rem; border-radius: .65rem; background: var(--jf-turquoise-500, #42bdc7); color: var(--jf-navy-900, #0b1c47); font-weight: 700; }
.jf-role-continue:disabled { opacity: .6; cursor: not-allowed; }
.jf-role-message { color: var(--jf-navy-900, #0b1c47); margin-top: 1rem; }
@media (max-width: 575px) { .jf-role-cards { grid-template-columns: 1fr; } .jf-role-card { gap: .5rem; padding: .9rem; } .jf-role-title { font-size: 1.4rem; } }
</style>
